import type { CSSProperties } from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebarStore } from '~/lib/store/sidebar';
import { useCommandPaletteStore } from '~/lib/store/command-palette';
import { menuPaths, getActiveMenuPath } from '~/lib/menu';
import { APP_TITLE, BRAND_NAME } from '~/lib/brand';
import { isAdminAssistantEnabled } from '~/lib/assistant/flag';
import { TopBar } from './top-bar';
import { Sidebar } from './sidebar';
import { CommandPalette } from './command-palette';

// Lazy so the assistant chunk is fetched ONLY when the flag is on (flag off = byte-inert, docs/ai/14).
const AssistantWidget = lazy(() => import('~/components/assistant/assistant-widget'));

/** Set document.title from the active menu route (longest path match). */
function useDocumentTitle() {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	useEffect(() => {
		const activePath = getActiveMenuPath(pathname);
		const match = activePath ? menuPaths.find(p => p.path === activePath) : undefined;
		document.title = match ? `${t(match.labelKey)} · ${BRAND_NAME}` : APP_TITLE;
	}, [pathname, t]);
}

/**
 * The chrome that wraps every authenticated page: a 3-zone CSS grid (topbar 48px +
 * sidebar `var(--sidebar-width)` + scrollable content). Used as a router layout —
 * feature routes render through the `<Outlet/>`.
 */
export function AppShell() {
	const collapsed = useSidebarStore(s => s.collapsed);
	const togglePalette = useCommandPaletteStore(s => s.toggle);
	useDocumentTitle();

	// Global ⌘K / Ctrl+K binding for the command palette.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
				e.preventDefault();
				togglePalette();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [togglePalette]);

	const style: CSSProperties = {
		gridTemplateRows: '48px 1fr',
		gridTemplateColumns: 'var(--sidebar-width) 1fr',
		gridTemplateAreas: '"topbar topbar" "sidebar content"',
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		['--sidebar-width' as any]: collapsed ? '64px' : '220px',
		transition: 'grid-template-columns 200ms ease-out'
	};
	return (
		<div className="grid h-screen w-screen overflow-hidden bg-background" style={style}>
			<header style={{ gridArea: 'topbar' }}>
				<TopBar />
			</header>
			<div style={{ gridArea: 'sidebar' }} className="min-h-0 overflow-hidden">
				<Sidebar />
			</div>
			<main style={{ gridArea: 'content' }} className="min-h-0 overflow-y-auto bg-background">
				<Outlet />
			</main>
			<CommandPalette />
			{isAdminAssistantEnabled() && (
				<Suspense fallback={null}>
					<AssistantWidget />
				</Suspense>
			)}
		</div>
	);
}
